import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';

/**
 * EventDrawer — Edit (or create) an event in a right-side slide-in.
 *
 * Three tabs:
 *   - Details: title, type, date, times, location, transport, capacity,
 *     resources, risk assessment, notes, status
 *   - Attendees: child picker that surfaces outings_excursions consent
 *     status per child. For type='trip', children without granted consent
 *     are blocked unless an override reason is supplied.
 *   - Staff: lead + supporting staff allocation from the org's active
 *     users.
 *
 * Props:
 *   eventId    – existing event id (read+update). Mutually exclusive with
 *                createNew.
 *   createNew  – when true, render an empty form for creating a new event.
 *   onClose    – close handler
 *   onSaved    – called after a successful save / delete (parent refreshes)
 */

const EMPTY_EVENT = {
  title: '',
  description: '',
  event_type: 'in_house',
  status: 'draft',
  event_date: new Date().toISOString().slice(0, 10),
  start_time: '',
  end_time: '',
  location: '',
  transport: '',
  capacity: '',
  lead_staff_id: '',
  supporting_staff_ids: [],
  attendee_child_ids: [],
  resources: [],
  risk_assessment: '',
  notes: '',
};

export function EventDrawer({ eventId, createNew, canManage = true, onClose, onSaved }) {
  const isNew = createNew && !eventId;

  const [tab, setTab] = useState('details');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_EVENT);
  const [original, setOriginal] = useState(null);  // for the loaded record (with attendees + staff blocks)
  const [consentCheck, setConsentCheck] = useState(null);
  const [allChildren, setAllChildren] = useState([]);
  const [allStaff, setAllStaff] = useState([]);
  const [childPickerOpen, setChildPickerOpen] = useState(false);
  const [childSearch, setChildSearch] = useState('');

  // --- Load data ---------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        // Always load people lookups (small enough)
        const [children, users] = await Promise.all([
          api.staffListChildren().catch(() => []),
          api.getAllUsers().catch(() => []),
        ]);
        if (cancelled) return;
        setAllChildren(Array.isArray(children) ? children : []);
        setAllStaff(
          (Array.isArray(users) ? users : [])
            .filter((u) => ['staff', 'room_leader', 'deputy_manager', 'org_admin', 'org_owner', 'admin', 'driver'].includes(u.role))
            .filter((u) => u.is_active !== false)
        );

        if (!isNew) {
          const ev = await api.getEvent(eventId);
          if (cancelled) return;
          setOriginal(ev);
          setForm({
            ...EMPTY_EVENT,
            ...ev,
            capacity: ev.capacity == null ? '' : String(ev.capacity),
            resources: ev.resources || [],
            supporting_staff_ids: ev.supporting_staff_ids || [],
            attendee_child_ids: ev.attendee_child_ids || [],
          });
          // Pre-load consent check (display only)
          api.getEventConsentCheck(eventId).then((cc) => { if (!cancelled) setConsentCheck(cc); }).catch(() => {});
        }
      } catch (err) {
        toast.error(err.message || 'Failed to load event');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [eventId, isNew]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const childMap = useMemo(() => {
    const m = new Map();
    allChildren.forEach((c) => m.set(c._id, c));
    return m;
  }, [allChildren]);
  const staffMap = useMemo(() => {
    const m = new Map();
    allStaff.forEach((u) => m.set(u._id, u));
    return m;
  }, [allStaff]);

  // --- Save / create -----------------------------------------------------
  async function handleSave() {
    setSaving(true);
    try {
      // Strip empty/falsey clutter and coerce capacity
      const payload = {
        ...form,
        capacity: form.capacity === '' ? null : Number(form.capacity),
        resources: form.resources || [],
      };
      // Don't send id/created_at etc on update; backend ignores them anyway
      let saved;
      if (isNew) {
        // Strip attendee/staff lists from initial creation — those are
        // managed via their dedicated tabs once the event exists.
        const initial = { ...payload };
        delete initial.attendee_child_ids;
        initial.supporting_staff_ids = initial.supporting_staff_ids || [];
        saved = await api.createEvent(initial);
        toast.success('Event created');
      } else {
        saved = await api.updateEvent(eventId, payload);
        toast.success('Event saved');
      }
      onSaved?.(saved);
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!eventId) return;
    if (!window.confirm(`Delete "${form.title || 'this event'}"? This cannot be undone.`)) return;
    try {
      await api.deleteEvent(eventId);
      toast.success('Event deleted');
      onSaved?.();
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    }
  }

  async function refreshAfterAttendees() {
    try {
      const [ev, cc] = await Promise.all([api.getEvent(eventId), api.getEventConsentCheck(eventId)]);
      setOriginal(ev);
      setForm((f) => ({ ...f, attendee_child_ids: ev.attendee_child_ids || [] }));
      setConsentCheck(cc);
    } catch { /* tolerated */ }
  }

  async function handleAddAttendees(ids) {
    if (!ids.length) return;
    try {
      const res = await api.addEventAttendees(eventId, ids);
      if (res.status === 'blocked') {
        // Show the override modal flow
        const reason = window.prompt(
          `Outings consent is missing for ${res.blocked.length} child(ren).\n\n`
          + res.blocked.map((b) => '• ' + childMap.get(b.child_id)?.full_name + ` (${b.reason})`).join('\n')
          + '\n\nEnter the reason to override (will be saved to the audit log):'
        );
        if (!reason || !reason.trim()) {
          toast.warning('No children added (consent missing & no override reason).');
          return;
        }
        const retry = await api.addEventAttendees(eventId, ids, {
          override_consent: true,
          override_reason: reason.trim(),
        });
        if (retry.status === 'ok') {
          toast.success(`Added ${retry.added.length}. ${retry.overrides_recorded} override(s) recorded.`);
        } else {
          toast.warning('Some children could not be added.');
        }
      } else {
        toast.success(`Added ${res.added.length} attendee${res.added.length === 1 ? '' : 's'}.`);
      }
      setChildPickerOpen(false);
      setChildSearch('');
      await refreshAfterAttendees();
    } catch (err) {
      toast.error(err.message || 'Failed to add attendees');
    }
  }

  async function handleRemoveAttendee(childId) {
    try {
      await api.removeEventAttendee(eventId, childId);
      toast.success('Attendee removed');
      await refreshAfterAttendees();
    } catch (err) {
      toast.error(err.message || 'Failed to remove attendee');
    }
  }

  // --- Render -----------------------------------------------------------
  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="event-drawer"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200,
        display: 'flex', justifyContent: 'flex-end',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        style={{
          width: 'min(720px, 96vw)', height: '100vh', background: 'hsl(var(--background))',
          boxShadow: '-12px 0 32px rgba(0,0,0,0.18)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid hsl(var(--border))', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: '1.1rem' }}>
              {isNew ? 'Create event' : (form.title || 'Edit event')}
            </h2>
            {!isNew && form.event_date && (
              <div style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))' }}>
                {form.event_date}{form.start_time ? ` · ${form.start_time}` : ''}{form.location ? ` · ${form.location}` : ''}
              </div>
            )}
          </div>
          <button type="button" className="btn btn-ghost" onClick={onClose} data-testid="event-drawer-close" aria-label="Close" style={{ padding: '0.4rem 0.7rem' }}>✕</button>
        </div>

        {/* Tab strip
            On narrow viewports there can be 5–6 tabs which overflow the
            drawer width. We keep the row horizontally scrollable, snap
            on each button, and layer a right-edge gradient hint so users
            understand there's more to scroll to. The hint disappears
            when the user has scrolled to the end (via scroll-snap +
            CSS-only `mask-image` would be cleaner but is finicky on iOS
            Safari, so we use the simpler stacked gradient overlay). */}
        {!isNew && (
          <div style={{ position: 'relative', borderBottom: '1px solid hsl(var(--border))', background: 'hsl(var(--muted) / 0.3)' }}>
            <div
              style={{
                display: 'flex',
                gap: '0.15rem',
                padding: '0 0.75rem',
                overflowX: 'auto',
                scrollbarWidth: 'thin',
                WebkitOverflowScrolling: 'touch',
                scrollSnapType: 'x proximity',
              }}
              data-testid="event-drawer-tab-strip"
            >
              {[
                { v: 'details',   label: '📝 Details' },
                { v: 'attendees', label: `👶 Attendees (${(form.attendee_child_ids || []).length})` },
                { v: 'register',  label: '✅ Register' },
                { v: 'staff',     label: '👥 Staff' },
                { v: 'media',     label: '📷 Media' },
                ...(canManage ? [{ v: 'notify', label: '✉️ Notify' }] : []),
              ].map((t) => (
                <button
                  key={t.v}
                  type="button"
                  onClick={() => setTab(t.v)}
                  data-testid={`event-tab-${t.v}`}
                  style={{
                    padding: '0.55rem 0.7rem', background: 'transparent', border: 'none',
                    borderBottom: tab === t.v ? '3px solid hsl(var(--primary))' : '3px solid transparent',
                    color: tab === t.v ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))',
                    fontWeight: tab === t.v ? 600 : 500, cursor: 'pointer', marginBottom: '-1px',
                    fontSize: '0.85rem',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    scrollSnapAlign: 'start',
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {/* Right-edge fade hint — purely decorative, signals scrollability. */}
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 1,
                width: 28,
                pointerEvents: 'none',
                background: 'linear-gradient(to right, hsl(var(--background) / 0), hsl(var(--background) / 0.7))',
              }}
            />
          </div>
        )}

        {/* Body */}
        <div style={{ padding: '1.25rem', flex: '1 1 auto', overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'hsl(var(--muted-foreground))' }}>Loading…</div>
          ) : (
            <>
              {(tab === 'details' || isNew) && (
                <DetailsForm form={form} setForm={setForm} eventId={isNew ? null : eventId} canManage={canManage} />
              )}
              {!isNew && tab === 'attendees' && (
                <AttendeesTab
                  eventType={form.event_type}
                  attendeeIds={form.attendee_child_ids || []}
                  consentCheck={consentCheck}
                  childMap={childMap}
                  onRemove={handleRemoveAttendee}
                  onAddOpen={() => setChildPickerOpen(true)}
                  canManage={canManage}
                />
              )}
              {!isNew && tab === 'staff' && (
                <StaffTab
                  form={form}
                  setForm={setForm}
                  staffOptions={allStaff}
                  staffMap={staffMap}
                  canManage={canManage}
                />
              )}
              {!isNew && tab === 'media' && (
                <MediaTab
                  eventId={eventId}
                  eventTitle={form.title}
                  attendeeIds={form.attendee_child_ids || []}
                  childMap={childMap}
                  canManage={canManage}
                />
              )}
              {!isNew && tab === 'register' && (
                <RegisterTab
                  eventId={eventId}
                  attendeeIds={form.attendee_child_ids || []}
                />
              )}
              {!isNew && tab === 'notify' && canManage && (
                <NotifyTab
                  eventId={eventId}
                  eventTitle={form.title}
                  attendeeCount={(form.attendee_child_ids || []).length}
                  notificationsLog={original?.notifications_log || form.notifications_log || []}
                  onLogged={async () => {
                    // Refresh original to get the latest notifications_log
                    try {
                      const ev = await api.getEvent(eventId);
                      setOriginal(ev);
                      setForm((f) => ({ ...f, notifications_log: ev.notifications_log || [] }));
                    } catch { /* tolerated */ }
                  }}
                />
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '0.85rem 1.25rem', borderTop: '1px solid hsl(var(--border))', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', background: 'hsl(var(--muted) / 0.2)' }}>
          {!isNew && canManage && (
            <button type="button" className="btn btn-ghost" onClick={handleDelete} data-testid="event-delete-btn" style={{ color: 'hsl(0 60% 40%)', marginRight: 'auto' }}>
              🗑 Delete
            </button>
          )}
          <button type="button" className="btn btn-ghost" onClick={onClose} data-testid="event-cancel-btn">
            {canManage ? 'Cancel' : 'Close'}
          </button>
          {canManage && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving || !form.title || !form.event_date}
              data-testid="event-save-btn"
            >
              {saving ? 'Saving…' : (isNew ? 'Create' : 'Save changes')}
            </button>
          )}
        </div>
      </div>

      {/* Child picker (overlay inside the drawer) */}
      {childPickerOpen && (
        <ChildPicker
          allChildren={allChildren}
          existingIds={form.attendee_child_ids || []}
          search={childSearch}
          setSearch={setChildSearch}
          onCancel={() => { setChildPickerOpen(false); setChildSearch(''); }}
          onSelect={(ids) => handleAddAttendees(ids)}
        />
      )}
    </div>
  );
}

// ---------------- Tabs ----------------

function DetailsForm({ form, setForm, eventId, canManage = true }) {
  function set(field, value) {
    if (!canManage) return;
    setForm({ ...form, [field]: value });
  }
  const ro = !canManage;
  return (
    <div data-testid="event-details-form">
      {ro && (
        <div
          style={{
            marginBottom: '0.75rem',
            padding: '0.55rem 0.75rem',
            background: 'hsl(45 95% 92%)',
            color: 'hsl(35 80% 30%)',
            borderRadius: 'var(--radius)',
            fontSize: '0.8rem',
          }}
          data-testid="event-details-readonly-banner"
        >
          🔒 Read-only — only room leaders & admin can edit event details. You can still run the register and post media.
        </div>
      )}
      <fieldset disabled={ro} style={{ border: 0, padding: 0, margin: 0 }}>
      <Row label="Title *">
        <input
          type="text"
          required
          value={form.title}
          onChange={(e) => set('title', e.target.value)}
          data-testid="event-title-input"
          style={inputStyle}
        />
      </Row>
      <Row label="Type">
        <select value={form.event_type} onChange={(e) => set('event_type', e.target.value)} style={inputStyle} data-testid="event-type-select">
          <option value="in_house">🏠 In-house (themed day / on-site activity)</option>
          <option value="trip">🚌 Trip (off-site — requires outings consent)</option>
          <option value="visitor">👋 Visitor (external visitor on-site)</option>
          <option value="parent_session">👪 Parent session (stay-and-play, open day)</option>
          <option value="celebration">🎉 Celebration</option>
          <option value="other">Other</option>
        </select>
      </Row>
      <Row label="Status">
        <select value={form.status} onChange={(e) => set('status', e.target.value)} style={inputStyle} data-testid="event-status-select">
          <option value="draft">Draft</option>
          <option value="scheduled">Scheduled</option>
          <option value="in_progress">In progress</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </Row>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.6rem' }}>
        <Row label="Date *">
          <input type="date" value={form.event_date} onChange={(e) => set('event_date', e.target.value)} style={inputStyle} data-testid="event-date-input" />
        </Row>
        <Row label="Start time">
          <input type="time" value={form.start_time || ''} onChange={(e) => set('start_time', e.target.value)} style={inputStyle} data-testid="event-start-input" />
        </Row>
        <Row label="End time">
          <input type="time" value={form.end_time || ''} onChange={(e) => set('end_time', e.target.value)} style={inputStyle} data-testid="event-end-input" />
        </Row>
      </div>
      <Row label="Location">
        <input type="text" value={form.location || ''} onChange={(e) => set('location', e.target.value)} style={inputStyle} placeholder="e.g. London Zoo, Regents Park" data-testid="event-location-input" />
      </Row>
      <Row label="Transport">
        <input type="text" value={form.transport || ''} onChange={(e) => set('transport', e.target.value)} style={inputStyle} placeholder="e.g. Walking / Coach / Bus" data-testid="event-transport-input" />
      </Row>
      <Row label="Capacity">
        <input type="number" min="0" value={form.capacity ?? ''} onChange={(e) => set('capacity', e.target.value)} style={inputStyle} placeholder="Optional" data-testid="event-capacity-input" />
      </Row>
      <Row label="Description">
        <textarea rows={2} value={form.description || ''} onChange={(e) => set('description', e.target.value)} style={{ ...inputStyle, resize: 'vertical' }} data-testid="event-description-input" />
      </Row>

      <ResourcesEditor form={form} setForm={setForm} />

      <Row label="Risk assessment notes">
        <textarea
          rows={4}
          value={form.risk_assessment || ''}
          onChange={(e) => set('risk_assessment', e.target.value)}
          placeholder="Identified hazards, mitigations, head-count cadence, emergency contacts, etc."
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
          data-testid="event-ra-input"
        />
      </Row>

      {/* RA file upload — only after the event has been saved (needs eventId) */}
      {eventId && (
        <RiskAssessmentFileRow eventId={eventId} canManage={canManage} />
      )}

      <Row label="Notes">
        <textarea rows={2} value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} style={{ ...inputStyle, resize: 'vertical' }} data-testid="event-notes-input" />
      </Row>
      </fieldset>
    </div>
  );
}

function ResourcesEditor({ form, setForm }) {
  const resources = form.resources || [];
  function update(i, patch) {
    const next = resources.slice();
    next[i] = { ...next[i], ...patch };
    setForm({ ...form, resources: next });
  }
  function add() {
    setForm({ ...form, resources: [...resources, { name: '', qty: '', procured: false }] });
  }
  function remove(i) {
    const next = resources.slice(); next.splice(i, 1);
    setForm({ ...form, resources: next });
  }
  return (
    <div style={{ marginBottom: '1rem' }} data-testid="event-resources">
      <div style={{ ...labelStyle, marginBottom: '0.3rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Resources needed</span>
        <button type="button" className="btn btn-ghost" onClick={add} style={{ fontSize: '0.78rem', padding: '0.2rem 0.55rem' }} data-testid="event-resource-add">+ Add</button>
      </div>
      {resources.length === 0 ? (
        <div style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>None listed.</div>
      ) : (
        resources.map((r, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto auto', gap: '0.4rem', marginBottom: '0.3rem', alignItems: 'center' }} data-testid={`event-resource-${i}`}>
            <input type="text" value={r.name || ''} onChange={(e) => update(i, { name: e.target.value })} placeholder="Resource name" style={inputStyle} />
            <input type="text" value={r.qty || ''} onChange={(e) => update(i, { qty: e.target.value })} placeholder="Qty / per child" style={inputStyle} />
            <label style={{ fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }} title="Tick when procured">
              <input type="checkbox" checked={!!r.procured} onChange={(e) => update(i, { procured: e.target.checked })} />
              Got it
            </label>
            <button type="button" className="btn btn-ghost" onClick={() => remove(i)} aria-label="Remove resource" style={{ padding: '0.25rem 0.45rem', color: 'hsl(0 60% 40%)' }}>×</button>
          </div>
        ))
      )}
    </div>
  );
}

function AttendeesTab({ eventType, attendeeIds, consentCheck, childMap, onRemove, onAddOpen, canManage = true }) {
  const isTrip = eventType === 'trip';
  const consentMap = useMemo(() => {
    const m = new Map();
    (consentCheck?.attendees || []).forEach((a) => m.set(a.child_id, a));
    return m;
  }, [consentCheck]);

  return (
    <div data-testid="event-attendees-tab">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
        <div>
          <strong style={{ fontSize: '0.95rem' }}>{attendeeIds.length} attendee{attendeeIds.length === 1 ? '' : 's'}</strong>
          {isTrip && (
            <div style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))' }}>
              Trip — outings consent is required per attendee.
            </div>
          )}
        </div>
        <button type="button" className="btn btn-primary" onClick={onAddOpen} data-testid="event-add-attendees-btn" style={{ padding: '0.4rem 0.85rem', display: canManage ? undefined : 'none' }}>+ Add children</button>
      </div>

      {consentCheck?.summary && (
        <div className="card" style={{ background: 'hsl(var(--muted) / 0.3)', marginBottom: '0.6rem', fontSize: '0.85rem' }} data-testid="consent-summary">
          ✅ Granted: <strong>{consentCheck.summary.granted}</strong>
          &nbsp;·&nbsp;❌ Refused: <strong>{consentCheck.summary.refused}</strong>
          &nbsp;·&nbsp;⚠️ Not recorded: <strong>{consentCheck.summary.missing_or_unrecorded}</strong>
        </div>
      )}

      {attendeeIds.length === 0 ? (
        <div className="empty-state" style={{ padding: '1.25rem' }} data-testid="attendees-empty">No attendees yet.</div>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }} data-testid="attendees-list">
          {attendeeIds.map((cid) => {
            const child = childMap.get(cid);
            const consent = consentMap.get(cid) || {};
            const consentBadge = isTrip ? consentPill(consent.outings_consent_granted, consent.consent_source) : null;
            return (
              <li key={cid} style={{ padding: '0.5rem 0', borderTop: '1px solid hsl(var(--border))', display: 'flex', alignItems: 'center', gap: '0.5rem' }} data-testid={`attendee-${cid}`}>
                <div style={{ flex: 1 }}>
                  <strong>{child?.full_name || consent.full_name || '(unknown)'}</strong>
                  {child?.year_group && <span style={{ marginLeft: '0.4rem', fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))' }}>{child.year_group}</span>}
                  {child?.allergies_count > 0 && <span style={{ marginLeft: '0.4rem', fontSize: '0.72rem', background: 'hsl(0 75% 92%)', color: 'hsl(0 60% 30%)', padding: '0.05rem 0.4rem', borderRadius: 999 }}>🥜 {child.allergies_count}</span>}
                  {child?.has_send && <span style={{ marginLeft: '0.3rem', fontSize: '0.72rem', background: 'hsl(260 60% 92%)', color: 'hsl(260 60% 30%)', padding: '0.05rem 0.4rem', borderRadius: 999 }}>🧩 SEND</span>}
                </div>
                {consentBadge}
                {canManage && (
                  <button type="button" className="btn btn-ghost" onClick={() => onRemove(cid)} aria-label={`Remove ${child?.full_name}`} style={{ padding: '0.2rem 0.45rem', color: 'hsl(0 60% 40%)' }} data-testid={`attendee-remove-${cid}`}>×</button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function consentPill(granted, source) {
  if (granted === true) {
    return <span style={{ background: 'hsl(140 70% 92%)', color: 'hsl(140 55% 26%)', padding: '0.15rem 0.55rem', borderRadius: 999, fontSize: '0.72rem', fontWeight: 600 }}>✓ Consent granted</span>;
  }
  if (granted === false) {
    return <span style={{ background: 'hsl(0 75% 94%)', color: 'hsl(0 60% 35%)', padding: '0.15rem 0.55rem', borderRadius: 999, fontSize: '0.72rem', fontWeight: 600 }}>✗ Consent refused</span>;
  }
  return (
    <span style={{ background: 'hsl(45 95% 92%)', color: 'hsl(35 80% 30%)', padding: '0.15rem 0.55rem', borderRadius: 999, fontSize: '0.72rem', fontWeight: 600 }} title={`Source: ${source}`}>
      ⚠️ Not recorded
    </span>
  );
}

function StaffTab({ form, setForm, staffOptions, staffMap, canManage = true }) {
  const lead = form.lead_staff_id ? staffMap.get(form.lead_staff_id) : null;
  function toggleSupport(uid) {
    if (!canManage) return;
    const list = form.supporting_staff_ids || [];
    if (list.includes(uid)) setForm({ ...form, supporting_staff_ids: list.filter((x) => x !== uid) });
    else setForm({ ...form, supporting_staff_ids: [...list, uid] });
  }
  const supporting = form.supporting_staff_ids || [];
  return (
    <div data-testid="event-staff-tab">
      <fieldset disabled={!canManage} style={{ border: 0, padding: 0, margin: 0 }}>
      <Row label="Lead staff">
        <select
          value={form.lead_staff_id || ''}
          onChange={(e) => setForm({ ...form, lead_staff_id: e.target.value || null })}
          style={inputStyle}
          data-testid="event-lead-select"
        >
          <option value="">— Select lead —</option>
          {staffOptions.map((u) => (
            <option key={u._id} value={u._id}>
              {u.full_name} ({u.role})
            </option>
          ))}
        </select>
      </Row>
      {lead && (
        <div style={{ marginBottom: '1rem', fontSize: '0.82rem', color: 'hsl(var(--muted-foreground))' }}>
          ⭐ Lead: <strong>{lead.full_name}</strong> · {lead.role}
        </div>
      )}
      <div style={labelStyle}>Supporting staff</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem' }} data-testid="event-supporting-grid">
        {staffOptions.filter((u) => u._id !== form.lead_staff_id).map((u) => {
          const on = supporting.includes(u._id);
          return (
            <label
              key={u._id}
              data-testid={`event-support-${u._id}`}
              style={{
                padding: '0.45rem 0.6rem',
                border: `1px solid ${on ? 'hsl(var(--primary))' : 'hsl(var(--border))'}`,
                borderRadius: 'var(--radius)',
                background: on ? 'hsl(var(--primary) / 0.07)' : '#fff',
                cursor: 'pointer', fontSize: '0.85rem',
              }}
            >
              <input
                type="checkbox"
                checked={on}
                onChange={() => toggleSupport(u._id)}
                style={{ marginRight: '0.4rem' }}
              />
              {u.full_name} <span style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.75rem' }}>· {u.role}</span>
            </label>
          );
        })}
      </div>
      </fieldset>
    </div>
  );
}

function ChildPicker({ allChildren, existingIds, search, setSearch, onCancel, onSelect }) {
  const [selected, setSelected] = useState(() => new Set());
  const available = useMemo(() => {
    const ex = new Set(existingIds);
    const needle = (search || '').trim().toLowerCase();
    return allChildren
      .filter((c) => !ex.has(c._id))
      .filter((c) => !needle || (c.full_name || '').toLowerCase().includes(needle));
  }, [allChildren, existingIds, search]);

  function toggle(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  return (
    <div
      role="dialog"
      data-testid="child-picker"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 250,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div style={{ background: 'hsl(var(--background))', borderRadius: 'var(--radius)', maxWidth: 500, width: 'min(500px, 96vw)', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '0.85rem 1rem', borderBottom: '1px solid hsl(var(--border))', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <strong>Add children</strong>
          <button type="button" className="btn btn-ghost" onClick={onCancel} aria-label="Close" style={{ padding: '0.2rem 0.5rem' }}>✕</button>
        </div>
        <div style={{ padding: '0.75rem 1rem' }}>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name…"
            style={{ ...inputStyle, width: '100%' }}
            data-testid="child-picker-search"
            autoFocus
          />
        </div>
        <div style={{ overflowY: 'auto', flex: 1, padding: '0 1rem' }}>
          {available.length === 0 ? (
            <div className="empty-state" style={{ padding: '1.5rem' }}>No matching children.</div>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {available.map((c) => (
                <li key={c._id} style={{ borderTop: '1px solid hsl(var(--border))' }}>
                  <label style={{ display: 'flex', alignItems: 'center', padding: '0.55rem 0', cursor: 'pointer', gap: '0.5rem' }} data-testid={`child-picker-option-${c._id}`}>
                    <input type="checkbox" checked={selected.has(c._id)} onChange={() => toggle(c._id)} />
                    <span style={{ fontWeight: 600 }}>{c.full_name}</span>
                    {c.year_group && <span style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.78rem' }}>· {c.year_group}</span>}
                    {c.allergies_count > 0 && <span style={{ fontSize: '0.7rem', background: 'hsl(0 75% 92%)', color: 'hsl(0 60% 30%)', padding: '0.05rem 0.4rem', borderRadius: 999 }}>🥜</span>}
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid hsl(var(--border))', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button type="button" className="btn btn-ghost" onClick={onCancel} data-testid="child-picker-cancel">Cancel</button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={selected.size === 0}
            onClick={() => onSelect(Array.from(selected))}
            data-testid="child-picker-submit"
          >
            Add {selected.size > 0 ? `${selected.size} child${selected.size === 1 ? '' : 'ren'}` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------- Layout helpers ----------------

/**
 * MediaTab — In-event photo/clip posting + post history.
 *
 * Designed so staff on the floor can drop media in 3 taps:
 *   1) Tap "📸 Add photos/clips" → native file picker (multi-select)
 *   2) Optional caption (defaults to event title)
 *   3) Tap Post — fans out to every selected attendee's parent feed
 *
 * Tagged attendees are pre-ticked from the event's attendee list so the
 * staff member doesn't need to assemble it. Quick toggle chips let them
 * exclude individual children before posting.
 */
// ---------------- Iteration C: Event Recap card ----------------

function RecapCard({ eventId, eventTitle, canManage = true, hasAnyMedia }) {
  const [recap, setRecap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  // ----- Background music state -----
  // `musicTracks` is the org-wide library (lazy-loaded the first time
  // the music UI opens). `withMusic` is the per-render toggle and
  // `selectedTrackId` is which track to mix in for the next render.
  const [musicTracks, setMusicTracks] = useState([]);
  const [musicLoaded, setMusicLoaded] = useState(false);
  const [withMusic, setWithMusic] = useState(false);
  const [selectedTrackId, setSelectedTrackId] = useState('');
  const [musicVolume, setMusicVolume] = useState(0.6);
  const [musicUploadBusy, setMusicUploadBusy] = useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getEventRecap(eventId);
      setRecap(data);
    } catch (err) {
      // Soft-fail (404 = no recap yet)
      setRecap(null);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  React.useEffect(() => { refresh(); }, [refresh]);

  // Poll while rendering.
  React.useEffect(() => {
    if (!recap) return undefined;
    if (recap.status !== 'queued' && recap.status !== 'rendering') return undefined;
    const t = setInterval(refresh, 3500);
    return () => clearInterval(t);
  }, [recap, refresh]);

  // Lazy-load music tracks once (only for users who can manage recaps).
  const loadMusicTracks = React.useCallback(async () => {
    if (!canManage || musicLoaded) return;
    try {
      const d = await api.listRecapMusic();
      const items = (d && d.items) || [];
      setMusicTracks(items);
      // Default to the most-recently uploaded track when the user opts in.
      if (!selectedTrackId && items.length > 0) setSelectedTrackId(items[0].id);
    } catch {
      // Soft fail — the toggle just stays empty.
    } finally {
      setMusicLoaded(true);
    }
  }, [canManage, musicLoaded, selectedTrackId]);

  async function handleMusicUpload(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setMusicUploadBusy(true);
    try {
      const out = await api.uploadRecapMusic(file, file.name.replace(/\.[^.]+$/, ''));
      toast.success(`Uploaded "${out.title}"`);
      // Refresh the library and auto-select the new track.
      const d = await api.listRecapMusic();
      const items = (d && d.items) || [];
      setMusicTracks(items);
      setSelectedTrackId(out.id);
      setWithMusic(true);
    } catch (err) {
      toast.error(err.message || 'Failed to upload track');
    } finally {
      setMusicUploadBusy(false);
      // Clear the input so the same file can be re-selected if needed.
      try { e.target.value = ''; } catch { /* ignore */ }
    }
  }

  async function handleDeleteMusic(trackId, title) {
    if (!window.confirm(`Remove "${title}" from the music library? Previously rendered recaps will still play normally.`)) return;
    try {
      await api.deleteRecapMusic(trackId);
      const d = await api.listRecapMusic();
      const items = (d && d.items) || [];
      setMusicTracks(items);
      if (selectedTrackId === trackId) {
        setSelectedTrackId(items[0]?.id || '');
        if (items.length === 0) setWithMusic(false);
      }
      toast.success('Track removed');
    } catch (err) {
      toast.error(err.message || 'Failed to remove track');
    }
  }

  async function handleGenerate() {
    if (!hasAnyMedia) {
      toast.error('Add some media to the event first.');
      return;
    }
    setBusy(true);
    try {
      const created = await api.generateEventRecap(eventId, {
        makePublic: true,
        shareTtlDays: 90,
        withMusic: withMusic && !!selectedTrackId,
        musicMediaId: withMusic ? selectedTrackId : undefined,
        musicVolume: withMusic ? musicVolume : undefined,
      });
      setRecap(created);
      toast.success('Recap queued — rendering will take ~10-30 seconds.');
    } catch (err) {
      toast.error(err.message || 'Failed to start recap render');
    } finally {
      setBusy(false);
    }
  }

  async function handleTogglePublic(nextVal) {
    setBusy(true);
    try {
      const updated = await api.updateEventRecapShare(eventId, { isPublic: nextVal });
      setRecap(updated);
      toast.success(nextVal ? 'Share link enabled' : 'Share link disabled');
    } catch (err) {
      toast.error(err.message || 'Failed to update share settings');
    } finally {
      setBusy(false);
    }
  }

  async function handleRotate() {
    if (!window.confirm('Rotate the share link? The previous link will stop working immediately.')) return;
    setBusy(true);
    try {
      const updated = await api.updateEventRecapShare(eventId, { rotateToken: true });
      setRecap(updated);
      toast.success('New share link minted');
    } catch (err) {
      toast.error(err.message || 'Failed to rotate link');
    } finally {
      setBusy(false);
    }
  }

  async function handleRegenerate() {
    if (!window.confirm('Re-render the recap with the current event media? The existing video will be replaced.')) return;
    setBusy(true);
    try {
      const created = await api.generateEventRecap(eventId, {
        makePublic: !!recap?.is_public,
        shareTtlDays: 90,
        withMusic: withMusic && !!selectedTrackId,
        musicMediaId: withMusic ? selectedTrackId : undefined,
        musicVolume: withMusic ? musicVolume : undefined,
      });
      setRecap(created);
      toast.success('Re-rendering queued.');
    } catch (err) {
      toast.error(err.message || 'Failed to re-render');
    } finally {
      setBusy(false);
    }
  }

  function copyShareLink() {
    if (!recap?.share_token) return;
    const url = api.buildRecapShareUrl(recap.share_token);
    try {
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Share link copied');
    } catch {
      toast.error('Could not copy link — please copy manually.');
    }
  }

  const isWorking = recap && (recap.status === 'queued' || recap.status === 'rendering');
  const isReady = recap && recap.status === 'ready';
  const isFailed = recap && recap.status === 'failed';

  // Compute share URL on-demand
  const shareUrl = recap?.share_token ? api.buildRecapShareUrl(recap.share_token) : null;
  const streamUrl = recap?.share_token ? api.buildRecapStreamUrl(recap.share_token) : null;
  const playUrl = isReady ? streamUrl : null;

  return (
    <div
      className="card"
      data-testid="event-recap-card"
      style={{
        marginBottom: '0.85rem',
        borderLeft: '4px solid hsl(var(--primary))',
        background: 'linear-gradient(180deg, hsl(var(--primary) / 0.06), hsl(var(--primary) / 0.02))',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', flexWrap: 'wrap', marginBottom: '0.4rem' }}>
        <strong style={{ fontSize: '0.98rem' }}>🎬 Event recap video</strong>
        {isWorking && <span style={{ fontSize: '0.72rem', background: 'hsl(45 95% 88%)', color: 'hsl(35 80% 30%)', padding: '0.1rem 0.5rem', borderRadius: 999, fontWeight: 600 }}>⏳ {recap.status}…</span>}
        {isReady && <span style={{ fontSize: '0.72rem', background: 'hsl(140 70% 90%)', color: 'hsl(140 55% 26%)', padding: '0.1rem 0.5rem', borderRadius: 999, fontWeight: 600 }}>✓ Ready</span>}
        {isFailed && <span style={{ fontSize: '0.72rem', background: 'hsl(0 75% 92%)', color: 'hsl(0 60% 35%)', padding: '0.1rem 0.5rem', borderRadius: 999, fontWeight: 600 }}>✗ Failed</span>}
      </div>

      {loading ? (
        <div style={{ fontSize: '0.82rem', color: 'hsl(var(--muted-foreground))' }}>Checking…</div>
      ) : !recap ? (
        <div>
          <p style={{ margin: '0 0 0.55rem 0', fontSize: '0.82rem', color: 'hsl(var(--muted-foreground))' }}>
            Auto-generate a slideshow video from this event's photos. Once ready, share a no-login link with parents.
          </p>
          {canManage && (
            <MusicSelector
              withMusic={withMusic}
              setWithMusic={(v) => { setWithMusic(v); if (v) loadMusicTracks(); }}
              tracks={musicTracks}
              selectedTrackId={selectedTrackId}
              setSelectedTrackId={setSelectedTrackId}
              volume={musicVolume}
              setVolume={setMusicVolume}
              onUpload={handleMusicUpload}
              onDelete={handleDeleteMusic}
              uploadBusy={musicUploadBusy}
              loadTracks={loadMusicTracks}
            />
          )}
          {canManage ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleGenerate}
              disabled={busy || !hasAnyMedia}
              data-testid="recap-generate-btn"
              style={{ padding: '0.45rem 0.9rem' }}
              title={hasAnyMedia ? '' : 'Post some media first'}
            >
              {busy ? 'Starting…' : '🎬 Generate recap'}
            </button>
          ) : (
            <div style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))', fontStyle: 'italic' }}>
              Only room leaders and admins can generate the recap video.
            </div>
          )}
        </div>
      ) : isWorking ? (
        <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
          <div className="recap-spinner" style={{ width: 18, height: 18, border: '2.5px solid hsl(var(--primary) / 0.25)', borderTopColor: 'hsl(var(--primary))', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          <div style={{ fontSize: '0.86rem', color: 'hsl(var(--foreground))' }}>
            Rendering… this usually takes <strong>10–30 seconds</strong> depending on how many photos you have.
          </div>
        </div>
      ) : isFailed ? (
        <div>
          <p style={{ margin: '0 0 0.55rem 0', fontSize: '0.84rem', color: 'hsl(0 55% 35%)' }}>
            {recap.error || 'Render failed.'}
          </p>
          {canManage && (
            <button type="button" className="btn btn-ghost" onClick={handleGenerate} disabled={busy} data-testid="recap-retry-btn" style={{ padding: '0.4rem 0.8rem' }}>
              🔁 Try again
            </button>
          )}
        </div>
      ) : (
        // READY
        <div>
          {playUrl && (
            <div style={{ marginBottom: '0.55rem', borderRadius: 'var(--radius)', overflow: 'hidden', background: '#000' }}>
              <video
                src={playUrl}
                controls
                preload="metadata"
                playsInline
                data-testid="recap-video"
                style={{ width: '100%', display: 'block', maxHeight: 280 }}
              />
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.45rem', alignItems: 'center', marginBottom: '0.4rem' }}>
            <small style={{ color: 'hsl(var(--muted-foreground))' }}>
              {recap.duration_seconds ? `${Math.round(recap.duration_seconds)}s` : '—'}
              {recap.width && recap.height ? ` · ${recap.width}×${recap.height}` : ''}
              {recap.size_bytes ? ` · ${formatBytes(recap.size_bytes)}` : ''}
              {typeof recap.view_count === 'number' ? ` · 👁 ${recap.view_count} view${recap.view_count === 1 ? '' : 's'}` : ''}
            </small>
          </div>

          {recap.is_public && shareUrl ? (
            <div style={{ background: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', borderRadius: 'var(--radius)', padding: '0.5rem 0.6rem', marginBottom: '0.5rem' }}>
              <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: 'hsl(var(--muted-foreground))', fontWeight: 600, marginBottom: '0.2rem', letterSpacing: '0.04em' }}>
                🔗 Public share link
              </div>
              <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  onClick={(e) => e.target.select()}
                  style={{ ...inputStyle, fontFamily: 'monospace', fontSize: '0.78rem', padding: '0.3rem 0.45rem', flex: 1, minWidth: 200 }}
                  data-testid="recap-share-url"
                />
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={copyShareLink}
                  data-testid="recap-copy-link-btn"
                  style={{ padding: '0.3rem 0.65rem', fontSize: '0.8rem' }}
                >
                  {copied ? '✓ Copied' : '📋 Copy'}
                </button>
                <a
                  href={shareUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-ghost"
                  style={{ padding: '0.3rem 0.65rem', fontSize: '0.8rem' }}
                  data-testid="recap-open-link"
                >↗ Open</a>
              </div>
              {recap.share_expires_at && (
                <div style={{ marginTop: '0.3rem', fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))' }}>
                  Expires {formatPostDate(recap.share_expires_at)}
                </div>
              )}
            </div>
          ) : (
            canManage && (
              <div style={{ fontSize: '0.82rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.4rem' }}>
                Share link is currently <strong>disabled</strong>.
              </div>
            )
          )}

          {canManage && (
            <>
              <MusicSelector
                withMusic={withMusic}
                setWithMusic={(v) => { setWithMusic(v); if (v) loadMusicTracks(); }}
                tracks={musicTracks}
                selectedTrackId={selectedTrackId}
                setSelectedTrackId={setSelectedTrackId}
                volume={musicVolume}
                setVolume={setMusicVolume}
                onUpload={handleMusicUpload}
                onDelete={handleDeleteMusic}
                uploadBusy={musicUploadBusy}
                loadTracks={loadMusicTracks}
                compact
              />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                {recap.is_public ? (
                  <>
                    <button type="button" className="btn btn-ghost" onClick={handleRotate} disabled={busy} data-testid="recap-rotate-btn" style={{ padding: '0.35rem 0.7rem', fontSize: '0.8rem' }}>🔁 Rotate link</button>
                    <button type="button" className="btn btn-ghost" onClick={() => handleTogglePublic(false)} disabled={busy} data-testid="recap-disable-share-btn" style={{ padding: '0.35rem 0.7rem', fontSize: '0.8rem' }}>🚫 Disable link</button>
                  </>
                ) : (
                  <button type="button" className="btn btn-ghost" onClick={() => handleTogglePublic(true)} disabled={busy} data-testid="recap-enable-share-btn" style={{ padding: '0.35rem 0.7rem', fontSize: '0.8rem' }}>🔗 Enable link</button>
                )}
                <button type="button" className="btn btn-ghost" onClick={handleRegenerate} disabled={busy} data-testid="recap-regenerate-btn" style={{ padding: '0.35rem 0.7rem', fontSize: '0.8rem', marginLeft: 'auto' }}>♻ Re-render</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * MusicSelector
 * --------------
 * Inline UI inside RecapCard that lets admins:
 *   - toggle background music on/off for the next render
 *   - pick a track from the org-wide library
 *   - adjust playback volume
 *   - upload a new MP3/M4A/WAV to the library
 *   - remove a track from the library
 *
 * Designed to be cheap when collapsed (no API call until the toggle is
 * flipped on) so it doesn't slow down the recap card initial render.
 */
function MusicSelector({
  withMusic, setWithMusic,
  tracks, selectedTrackId, setSelectedTrackId,
  volume, setVolume,
  onUpload, onDelete,
  uploadBusy,
  loadTracks,
  compact = false,
}) {
  const fileInputId = React.useId();
  const selected = tracks.find(t => t.id === selectedTrackId) || null;

  return (
    <div
      data-testid="recap-music-selector"
      style={{
        border: '1px solid hsl(var(--border))',
        borderRadius: 10,
        padding: '0.55rem 0.7rem',
        marginBottom: '0.55rem',
        background: 'hsl(var(--background))',
      }}
    >
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.86rem', fontWeight: 600 }}>
        <input
          type="checkbox"
          checked={!!withMusic}
          onChange={(e) => setWithMusic(e.target.checked)}
          data-testid="recap-music-toggle"
        />
        🎵 Add background music
        {withMusic && tracks.length === 0 && (
          <span style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))', fontWeight: 400 }}>
            — upload a track below to enable
          </span>
        )}
      </label>

      {withMusic && (
        <div style={{ marginTop: '0.55rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {tracks.length > 0 ? (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <select
                value={selectedTrackId}
                onChange={(e) => setSelectedTrackId(e.target.value)}
                data-testid="recap-music-select"
                style={{ flex: 1, minWidth: 180, padding: '0.3rem 0.45rem', borderRadius: 6, border: '1px solid hsl(var(--border))', fontSize: '0.84rem', background: '#fff' }}
              >
                {tracks.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.title}{t.size_bytes ? ` · ${formatBytes(t.size_bytes)}` : ''}
                  </option>
                ))}
              </select>
              {selected && (
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => onDelete(selected.id, selected.title)}
                  data-testid="recap-music-delete-btn"
                  title="Remove this track from the library"
                  style={{ padding: '0.25rem 0.55rem', fontSize: '0.78rem' }}
                >🗑</button>
              )}
            </div>
          ) : (
            <div style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))' }}>
              No music tracks in the library yet — upload one to get started.
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
            <label htmlFor={`vol-${fileInputId}`} style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))', minWidth: 56 }}>
              Volume
            </label>
            <input
              id={`vol-${fileInputId}`}
              type="range"
              min="0"
              max="1.2"
              step="0.05"
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              data-testid="recap-music-volume"
              style={{ flex: 1, minWidth: 120 }}
            />
            <span style={{ fontSize: '0.74rem', color: 'hsl(var(--muted-foreground))', minWidth: 38, textAlign: 'right' }}>
              {Math.round(volume * 100)}%
            </span>
          </div>

          {!compact && (
            <div>
              <label
                htmlFor={fileInputId}
                className="btn btn-ghost"
                style={{ padding: '0.3rem 0.7rem', fontSize: '0.8rem', display: 'inline-block', cursor: uploadBusy ? 'wait' : 'pointer' }}
                data-testid="recap-music-upload-label"
              >
                {uploadBusy ? 'Uploading…' : '⬆ Upload track'}
              </label>
              <input
                id={fileInputId}
                type="file"
                accept="audio/mpeg,audio/mp3,audio/x-m4a,audio/mp4,audio/wav,audio/x-wav,audio/ogg,audio/aac,.mp3,.m4a,.wav,.ogg,.aac"
                onChange={onUpload}
                disabled={uploadBusy}
                onClick={() => loadTracks && loadTracks()}
                data-testid="recap-music-upload-input"
                style={{ display: 'none' }}
              />
              <small style={{ marginLeft: '0.5rem', color: 'hsl(var(--muted-foreground))', fontSize: '0.72rem' }}>
                MP3/M4A/WAV · max 15 MB
              </small>
            </div>
          )}
        </div>
      )}
    </div>
  );
}



function MediaTab({ eventId, eventTitle, attendeeIds, childMap, canManage = true }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [files, setFiles] = useState([]);           // staged files
  const [caption, setCaption] = useState('');
  const [tagged, setTagged] = useState(() => new Set(attendeeIds));
  const [posting, setPosting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });

  // Resync tagged set if the attendee list changes while the tab is open.
  React.useEffect(() => {
    setTagged(new Set(attendeeIds));
  }, [attendeeIds]);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.listEventMoments(eventId);
      setPosts(data?.posts || []);
    } catch (err) {
      toast.error(err.message || 'Failed to load media');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  React.useEffect(() => { refresh(); }, [refresh]);

  function onFilesPicked(e) {
    const list = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...list]);
  }
  function removeStagedFile(i) {
    setFiles((prev) => { const next = prev.slice(); next.splice(i, 1); return next; });
  }
  function toggleTagged(childId) {
    setTagged((prev) => {
      const next = new Set(prev);
      if (next.has(childId)) next.delete(childId); else next.add(childId);
      return next;
    });
  }

  async function handlePost() {
    if (files.length === 0) {
      toast.error('Add at least one photo or clip first.');
      return;
    }
    if (tagged.size === 0) {
      toast.error('Tick at least one attendee to tag.');
      return;
    }
    setPosting(true);
    setUploadProgress({ done: 0, total: files.length });
    try {
      // Upload sequentially so progress feedback is accurate; can switch
      // to Promise.all if perf becomes an issue.
      const mediaIds = [];
      for (let i = 0; i < files.length; i++) {
        const res = await api.uploadMomentMedia(files[i]);
        if (res?.media_id) mediaIds.push(res.media_id);
        setUploadProgress({ done: i + 1, total: files.length });
      }
      if (mediaIds.length === 0) throw new Error('No media uploaded.');

      const result = await api.createEventMoments(eventId, {
        media_ids: mediaIds,
        caption: caption.trim() || undefined,
        child_ids: Array.from(tagged),
      });
      toast.success(
        `Posted ${mediaIds.length} ${mediaIds.length === 1 ? 'item' : 'items'} to ${result.tagged_children} parent feed${result.tagged_children === 1 ? '' : 's'}.`
      );
      setFiles([]);
      setCaption('');
      setUploadProgress({ done: 0, total: 0 });
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to post');
    } finally {
      setPosting(false);
    }
  }

  return (
    <div data-testid="event-media-tab">
      {/* Recap video card — generate / preview / share link */}
      <RecapCard
        eventId={eventId}
        eventTitle={eventTitle}
        canManage={canManage}
        hasAnyMedia={posts.length > 0}
      />

      {/* Composer */}
      <div className="card" style={{ marginBottom: '0.85rem', background: 'hsl(var(--primary) / 0.04)' }} data-testid="event-media-composer">
        <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.95rem' }}>📸 Share moments from {eventTitle || 'this event'}</h4>

        {/* Hidden file input + visible CTA */}
        <input
          id={`media-file-input-${eventId}`}
          type="file"
          accept="image/*,video/*"
          multiple
          capture="environment"
          onChange={onFilesPicked}
          style={{ display: 'none' }}
          data-testid="event-media-file-input"
        />
        <label
          htmlFor={`media-file-input-${eventId}`}
          className="btn btn-primary"
          data-testid="event-media-add-btn"
          style={{ display: 'inline-block', padding: '0.55rem 1rem', cursor: 'pointer', marginBottom: '0.6rem' }}
        >
          📸 Add photos / clips
        </label>
        <span style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))', marginLeft: '0.55rem' }}>
          {files.length > 0 ? `${files.length} file${files.length === 1 ? '' : 's'} ready to post` : 'Multi-select supported on mobile + desktop'}
        </span>

        {/* Staged previews */}
        {files.length > 0 && (
          <div style={{ marginBottom: '0.6rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '0.4rem' }} data-testid="event-media-staged">
            {files.map((f, i) => {
              const url = f.type?.startsWith('video/')
                ? null
                : URL.createObjectURL(f);
              return (
                <div key={i} style={{ position: 'relative', aspectRatio: '1 / 1', borderRadius: 6, overflow: 'hidden', background: 'hsl(var(--muted))' }}>
                  {url ? (
                    <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onLoad={() => URL.revokeObjectURL(url)} />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: '1.5rem' }}>🎬</div>
                  )}
                  <button
                    type="button"
                    onClick={() => removeStagedFile(i)}
                    aria-label="Remove"
                    style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', fontSize: '0.75rem' }}
                  >×</button>
                </div>
              );
            })}
          </div>
        )}

        <textarea
          rows={2}
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder={`Caption (defaults to “${eventTitle || 'this event'}”)`}
          style={{ width: '100%', padding: '0.5rem 0.65rem', border: '1px solid hsl(var(--border))', borderRadius: 'var(--radius)', fontSize: '0.9rem', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }}
          data-testid="event-media-caption"
        />

        {/* Tagged-attendee chips */}
        {attendeeIds.length > 0 ? (
          <div style={{ marginTop: '0.5rem' }}>
            <div style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.25rem', fontWeight: 600 }}>
              Tag attendees ({tagged.size}/{attendeeIds.length})
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }} data-testid="event-media-tag-chips">
              {attendeeIds.map((cid) => {
                const child = childMap.get(cid);
                const on = tagged.has(cid);
                return (
                  <button
                    key={cid}
                    type="button"
                    onClick={() => toggleTagged(cid)}
                    data-testid={`event-media-tag-${cid}`}
                    style={{
                      padding: '0.25rem 0.55rem',
                      borderRadius: 999,
                      border: `1px solid ${on ? 'hsl(var(--primary))' : 'hsl(var(--border))'}`,
                      background: on ? 'hsl(var(--primary) / 0.12)' : '#fff',
                      color: on ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))',
                      fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    {on ? '✓ ' : ''}{child?.full_name || cid.slice(0, 6)}
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div style={{ marginTop: '0.6rem', fontSize: '0.82rem', color: 'hsl(35 80% 30%)', background: 'hsl(45 95% 92%)', padding: '0.45rem 0.6rem', borderRadius: 6 }}>
            ⚠️ Add children on the Attendees tab before posting media.
          </div>
        )}

        <div style={{ marginTop: '0.7rem', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.5rem' }}>
          {posting && uploadProgress.total > 0 && (
            <span style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))' }}>
              Uploading {uploadProgress.done}/{uploadProgress.total}…
            </span>
          )}
          <button
            type="button"
            className="btn btn-primary"
            onClick={handlePost}
            disabled={posting || files.length === 0 || tagged.size === 0}
            data-testid="event-media-post-btn"
            style={{ padding: '0.5rem 1rem' }}
          >
            {posting ? 'Posting…' : `🚀 Post${files.length > 0 ? ' (' + files.length + ')' : ''}`}
          </button>
        </div>
      </div>

      {/* Existing posts */}
      <h4 style={{ margin: '1rem 0 0.45rem 0', fontSize: '0.9rem', color: 'hsl(var(--muted-foreground))' }}>Posts so far ({posts.length})</h4>

      {loading ? (
        <div style={{ padding: '1.5rem', textAlign: 'center', color: 'hsl(var(--muted-foreground))' }}>Loading posts…</div>
      ) : posts.length === 0 ? (
        <div className="empty-state" style={{ padding: '1.5rem' }} data-testid="event-media-empty">
          No media posted yet. Tap <strong>📸 Add photos / clips</strong> above to share the first one.
        </div>
      ) : (
        <div data-testid="event-media-posts">
          {posts.map((p) => (
            <div
              key={p.upload_batch_id || (p.moment_ids || []).join('-')}
              className="card"
              style={{ marginBottom: '0.6rem' }}
              data-testid={`event-media-post-${p.upload_batch_id || (p.moment_ids || [])[0]}`}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', alignItems: 'flex-start' }}>
                <strong style={{ fontSize: '0.9rem' }}>{p.caption || eventTitle}</strong>
                <span style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))' }}>
                  {formatPostDate(p.created_at)}
                </span>
              </div>
              {p.posted_by_name && (
                <div style={{ fontSize: '0.74rem', color: 'hsl(var(--muted-foreground))' }}>by {p.posted_by_name}</div>
              )}
              {p.media_file_ids?.length > 0 && (
                <div style={{ marginTop: '0.4rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: '0.35rem' }}>
                  {p.media_file_ids.map((mid) => (
                    <div key={mid} style={{ aspectRatio: '1 / 1', overflow: 'hidden', borderRadius: 6, background: 'hsl(var(--muted))' }}>
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/moments/media/${mid}`}
                        alt=""
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    </div>
                  ))}
                </div>
              )}
              {p.tagged_children?.length > 0 && (
                <div style={{ marginTop: '0.4rem', display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                  {p.tagged_children.map((tc) => (
                    <span key={tc.child_id} style={{ fontSize: '0.7rem', background: 'hsl(var(--muted) / 0.5)', color: 'hsl(var(--foreground))', padding: '0.1rem 0.4rem', borderRadius: 999 }}>
                      {tc.full_name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatPostDate(value) {
  if (!value) return '';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return value; }
}

// ---------------- Iteration B: Risk Assessment file uploader ----------------

function RiskAssessmentFileRow({ eventId, canManage = true }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getEventRiskAssessment(eventId);
      setFile(data);
    } catch (err) {
      // Soft-fail — most likely "no RA attached" which we treat as null
      setFile(null);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  React.useEffect(() => { refresh(); }, [refresh]);

  async function handleUpload(picked) {
    if (!picked) return;
    setBusy(true);
    try {
      await api.uploadAndAttachEventRiskAssessment(eventId, picked);
      toast.success('Risk assessment uploaded');
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove() {
    if (!window.confirm('Remove the attached risk assessment file?')) return;
    setBusy(true);
    try {
      await api.clearEventRiskAssessment(eventId);
      toast.success('Risk assessment removed');
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Remove failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginBottom: '0.9rem' }} data-testid="event-ra-file-row">
      <div style={labelStyle}>Risk assessment file (PDF / DOC / image)</div>
      {loading ? (
        <div style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>Checking…</div>
      ) : file ? (
        <div style={{
          padding: '0.6rem 0.75rem',
          border: '1px solid hsl(var(--border))',
          borderRadius: 'var(--radius)',
          background: 'hsl(140 60% 96%)',
          display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap',
        }} data-testid="event-ra-file-attached">
          <span style={{ fontSize: '1.1rem' }}>📄</span>
          <div style={{ flex: 1, minWidth: 160 }}>
            <strong style={{ fontSize: '0.88rem' }}>{file.original_filename || 'document'}</strong>
            {file.size != null && (
              <div style={{ fontSize: '0.74rem', color: 'hsl(var(--muted-foreground))' }}>
                {formatBytes(file.size)}{file.content_type ? ` · ${file.content_type}` : ''}
              </div>
            )}
          </div>
          {file.url && (
            <a
              href={file.url}
              target="_blank"
              rel="noreferrer"
              className="btn btn-ghost"
              style={{ padding: '0.3rem 0.6rem', fontSize: '0.82rem' }}
              data-testid="event-ra-file-open"
            >⬇ Download</a>
          )}
          {canManage && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={handleRemove}
              disabled={busy}
              data-testid="event-ra-file-remove"
              style={{ padding: '0.3rem 0.6rem', fontSize: '0.82rem', color: 'hsl(0 60% 40%)' }}
            >Remove</button>
          )}
        </div>
      ) : canManage ? (
        <div>
          <input
            id={`event-ra-file-${eventId}`}
            type="file"
            accept="application/pdf,.pdf,.doc,.docx,image/png,image/jpeg,image/webp"
            onChange={(e) => handleUpload(e.target.files?.[0])}
            style={{ display: 'none' }}
            data-testid="event-ra-file-input"
          />
          <label
            htmlFor={`event-ra-file-${eventId}`}
            className="btn btn-ghost"
            style={{ display: 'inline-block', padding: '0.45rem 0.85rem', cursor: busy ? 'wait' : 'pointer', border: '1px dashed hsl(var(--border))', borderRadius: 'var(--radius)', fontSize: '0.85rem' }}
            data-testid="event-ra-file-upload-btn"
          >
            {busy ? 'Uploading…' : '📎 Upload risk assessment'}
          </label>
          <span style={{ marginLeft: '0.6rem', fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))' }}>
            PDF, DOC, DOCX, PNG, JPEG or WEBP
          </span>
        </div>
      ) : (
        <div style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))', fontStyle: 'italic' }}>
          No risk assessment attached yet.
        </div>
      )}
    </div>
  );
}

function formatBytes(n) {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// ---------------- Iteration B: Register tab ----------------

function RegisterTab({ eventId, attendeeIds }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);            // [{child_id, full_name, status, note...}]
  const [summary, setSummary] = useState(null);
  const [dirty, setDirty] = useState({});          // { child_id: { status, note } }
  const [headcounts, setHeadcounts] = useState([]);
  const [hcCount, setHcCount] = useState('');
  const [hcLocation, setHcLocation] = useState('');
  const [hcNote, setHcNote] = useState('');
  const [hcBusy, setHcBusy] = useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const [att, hc] = await Promise.all([
        api.getEventAttendance(eventId),
        api.listEventHeadcounts(eventId),
      ]);
      setRows(att.rows || []);
      setSummary(att.summary || null);
      setHeadcounts(hc.entries || []);
      setDirty({});
    } catch (err) {
      toast.error(err.message || 'Failed to load register');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  React.useEffect(() => { refresh(); }, [refresh]);

  function setStatusLocal(childId, status) {
    setDirty((prev) => ({ ...prev, [childId]: { ...(prev[childId] || {}), status } }));
  }
  function setNoteLocal(childId, note) {
    setDirty((prev) => ({ ...prev, [childId]: { ...(prev[childId] || {}), note } }));
  }

  function effective(row) {
    const d = dirty[row.child_id];
    return {
      status: d?.status ?? row.status,
      note:   d?.note ?? row.note ?? '',
    };
  }

  async function saveAll() {
    const updates = Object.entries(dirty)
      .map(([cid, val]) => ({ child_id: cid, status: val.status, note: val.note }))
      .filter((u) => !!u.status);
    if (updates.length === 0) {
      toast.message('Nothing to save');
      return;
    }
    setSaving(true);
    try {
      const res = await api.bulkUpsertEventAttendance(eventId, updates);
      toast.success(`Saved ${res.written}${res.rejected?.length ? ` (${res.rejected.length} rejected)` : ''}`);
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function markAllPresent() {
    setSaving(true);
    try {
      const updates = rows.map((r) => ({ child_id: r.child_id, status: 'present' }));
      const res = await api.bulkUpsertEventAttendance(eventId, updates);
      toast.success(`Marked ${res.written} present`);
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function addHeadcount() {
    const n = parseInt(hcCount, 10);
    if (Number.isNaN(n) || n < 0) {
      toast.error('Enter a valid headcount number');
      return;
    }
    setHcBusy(true);
    try {
      await api.createEventHeadcount(eventId, {
        count: n,
        ...(hcLocation ? { location: hcLocation } : {}),
        ...(hcNote ? { note: hcNote } : {}),
      });
      setHcCount('');
      setHcLocation('');
      setHcNote('');
      toast.success('Headcount recorded');
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to record headcount');
    } finally {
      setHcBusy(false);
    }
  }

  if (attendeeIds.length === 0) {
    return (
      <div className="empty-state" style={{ padding: '1.5rem' }} data-testid="register-empty">
        Add children on the <strong>Attendees</strong> tab before running the register.
      </div>
    );
  }

  const dirtyCount = Object.keys(dirty).length;
  const expected = rows.length;

  return (
    <div data-testid="event-register-tab">
      {/* Summary + actions */}
      <div className="card" style={{ background: 'hsl(var(--muted) / 0.3)', marginBottom: '0.75rem' }} data-testid="register-summary">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center' }}>
          <strong style={{ fontSize: '0.95rem' }}>Today's register</strong>
          {summary && (
            <span style={{ fontSize: '0.82rem', color: 'hsl(var(--muted-foreground))' }}>
              ✅ {summary.present} present · 🕘 {summary.late} late · ❌ {summary.absent} absent · ⏳ {summary.pending} pending
            </span>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem' }}>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={markAllPresent}
              disabled={saving || loading}
              data-testid="register-mark-all-present"
              style={{ padding: '0.35rem 0.7rem', fontSize: '0.82rem' }}
            >✅ Mark all present</button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={saveAll}
              disabled={saving || loading || dirtyCount === 0}
              data-testid="register-save-btn"
              style={{ padding: '0.35rem 0.85rem', fontSize: '0.82rem' }}
            >{saving ? 'Saving…' : `Save${dirtyCount > 0 ? ` (${dirtyCount})` : ''}`}</button>
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: '1.5rem', textAlign: 'center', color: 'hsl(var(--muted-foreground))' }}>Loading register…</div>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }} data-testid="register-list">
          {rows.map((r) => {
            const eff = effective(r);
            return (
              <li key={r.child_id} style={{ padding: '0.55rem 0', borderTop: '1px solid hsl(var(--border))' }} data-testid={`register-row-${r.child_id}`}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 140 }}>
                    <strong>{r.full_name}</strong>
                    {r.year_group && <span style={{ marginLeft: '0.35rem', fontSize: '0.74rem', color: 'hsl(var(--muted-foreground))' }}>{r.year_group}</span>}
                    {r.marked_by_name && (
                      <div style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))' }}>
                        Last by {r.marked_by_name}{r.marked_at ? ' · ' + formatPostDate(r.marked_at) : ''}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '0.25rem' }} data-testid={`register-status-${r.child_id}`}>
                    {[
                      { v: 'present', label: '✅ Present', bg: 'hsl(140 70% 92%)', fg: 'hsl(140 55% 26%)' },
                      { v: 'late',    label: '🕘 Late',    bg: 'hsl(45 95% 92%)',  fg: 'hsl(35 80% 30%)' },
                      { v: 'absent',  label: '❌ Absent',  bg: 'hsl(0 75% 94%)',   fg: 'hsl(0 60% 35%)' },
                    ].map((opt) => {
                      const on = eff.status === opt.v;
                      return (
                        <button
                          key={opt.v}
                          type="button"
                          onClick={() => setStatusLocal(r.child_id, opt.v)}
                          data-testid={`register-set-${opt.v}-${r.child_id}`}
                          style={{
                            padding: '0.3rem 0.65rem', borderRadius: 999, fontSize: '0.76rem', fontWeight: 600,
                            border: `1px solid ${on ? opt.fg : 'hsl(var(--border))'}`,
                            background: on ? opt.bg : '#fff',
                            color: on ? opt.fg : 'hsl(var(--muted-foreground))',
                            cursor: 'pointer',
                          }}
                        >{opt.label}</button>
                      );
                    })}
                  </div>
                </div>
                {(eff.status === 'late' || eff.status === 'absent' || (r.note && eff.status !== 'present')) && (
                  <input
                    type="text"
                    value={eff.note}
                    onChange={(e) => setNoteLocal(r.child_id, e.target.value)}
                    placeholder="Optional note (reason, time, contact)"
                    style={{ ...inputStyle, marginTop: '0.35rem', fontSize: '0.82rem', padding: '0.35rem 0.55rem' }}
                    data-testid={`register-note-${r.child_id}`}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Headcount panel */}
      <div className="card" style={{ marginTop: '1.1rem', background: 'hsl(220 70% 97%)' }} data-testid="headcount-panel">
        <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.95rem' }}>🔢 Head-count log</h4>
        <p style={{ margin: '0 0 0.6rem 0', fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))' }}>
          Record a count at each checkpoint (departure, arrival, after lunch, return). Expected: <strong>{expected}</strong>.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr 1fr auto', gap: '0.4rem', alignItems: 'end' }}>
          <Row label="Count">
            <input
              type="number"
              min="0"
              value={hcCount}
              onChange={(e) => setHcCount(e.target.value)}
              placeholder="0"
              style={inputStyle}
              data-testid="headcount-count-input"
            />
          </Row>
          <Row label="Location">
            <input
              type="text"
              value={hcLocation}
              onChange={(e) => setHcLocation(e.target.value)}
              placeholder="e.g. Coach pickup"
              style={inputStyle}
              data-testid="headcount-location-input"
            />
          </Row>
          <Row label="Note">
            <input
              type="text"
              value={hcNote}
              onChange={(e) => setHcNote(e.target.value)}
              placeholder="Optional"
              style={inputStyle}
              data-testid="headcount-note-input"
            />
          </Row>
          <button
            type="button"
            className="btn btn-primary"
            onClick={addHeadcount}
            disabled={hcBusy || hcCount === ''}
            data-testid="headcount-add-btn"
            style={{ padding: '0.55rem 0.85rem', alignSelf: 'end' }}
          >{hcBusy ? '…' : '+ Add'}</button>
        </div>

        {headcounts.length > 0 ? (
          <ul style={{ listStyle: 'none', padding: 0, marginTop: '0.7rem' }} data-testid="headcount-list">
            {headcounts.map((h) => {
              const variance = h.expected != null ? h.count - h.expected : 0;
              const ok = variance === 0;
              return (
                <li key={h.id} style={{ padding: '0.45rem 0', borderTop: '1px solid hsl(var(--border))', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.85rem', minWidth: 70 }}>
                    <strong>{h.count}</strong>
                    {h.expected != null && <span style={{ color: 'hsl(var(--muted-foreground))' }}> / {h.expected}</span>}
                  </span>
                  {!ok && h.expected != null && (
                    <span style={{ background: 'hsl(0 75% 94%)', color: 'hsl(0 60% 35%)', padding: '0.1rem 0.45rem', borderRadius: 999, fontSize: '0.7rem', fontWeight: 600 }}>
                      {variance > 0 ? '+' : ''}{variance}
                    </span>
                  )}
                  <span style={{ fontSize: '0.82rem', flex: 1 }}>
                    {h.location ? <strong>{h.location}</strong> : <em style={{ color: 'hsl(var(--muted-foreground))' }}>—</em>}
                    {h.note ? <span style={{ color: 'hsl(var(--muted-foreground))' }}> · {h.note}</span> : null}
                  </span>
                  <span style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))' }}>
                    {formatPostDate(h.recorded_at)}{h.recorded_by_name ? ' · ' + h.recorded_by_name : ''}
                  </span>
                </li>
              );
            })}
          </ul>
        ) : (
          <div style={{ marginTop: '0.5rem', fontSize: '0.82rem', color: 'hsl(var(--muted-foreground))' }}>
            No counts logged yet.
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------- Iteration B: Notify parents tab ----------------

function NotifyTab({ eventId, eventTitle, attendeeCount, notificationsLog, onLogged }) {
  const [kind, setKind] = useState('reminder');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const kindOptions = [
    { v: 'scheduled', label: '📣 Announce', helper: 'Tell parents the event is scheduled and share key details.' },
    { v: 'reminder',  label: '⏰ Reminder',  helper: 'Send a pre-event reminder with all the key details.' },
    { v: 'cancelled', label: '🚫 Cancellation', helper: 'Notify parents that the event has been cancelled.' },
    { v: 'custom',    label: '✏️ Custom update', helper: 'Send a free-form update — use the message box below.' },
  ];

  async function send() {
    if (attendeeCount === 0) {
      toast.error('Add attendees first.');
      return;
    }
    if (kind === 'custom' && !message.trim()) {
      toast.error('Custom updates need a message.');
      return;
    }
    const confirmation = (kind === 'cancelled')
      ? `Send a CANCELLATION email to parents of all ${attendeeCount} attendee(s)?`
      : `Send "${kindOptions.find((k) => k.v === kind)?.label}" email to parents of all ${attendeeCount} attendee(s)?`;
    if (!window.confirm(confirmation)) return;

    setBusy(true);
    try {
      const res = await api.notifyEventParents(eventId, {
        kind,
        ...(message.trim() ? { message: message.trim() } : {}),
      });
      const s = res.summary || {};
      toast.success(`Sent ${s.ok || 0}/${s.recipients || 0} email(s)${s.failed ? ` · ${s.failed} failed` : ''}${s.skipped ? ` · ${s.skipped} skipped` : ''}`);
      setMessage('');
      await onLogged?.();
    } catch (err) {
      toast.error(err.message || 'Failed to send notifications');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="event-notify-tab">
      <div className="card" style={{ marginBottom: '0.9rem', background: 'hsl(var(--primary) / 0.04)' }} data-testid="notify-composer">
        <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.95rem' }}>✉️ Notify parents about {eventTitle || 'this event'}</h4>
        <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.82rem', color: 'hsl(var(--muted-foreground))' }}>
          Sends a branded email to <strong>{attendeeCount}</strong> attendee{attendeeCount === 1 ? '' : 's'}' primary parent. Each send is audit-logged below.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.4rem', marginBottom: '0.7rem' }} data-testid="notify-kind-grid">
          {kindOptions.map((k) => {
            const on = kind === k.v;
            return (
              <button
                key={k.v}
                type="button"
                onClick={() => setKind(k.v)}
                data-testid={`notify-kind-${k.v}`}
                style={{
                  textAlign: 'left',
                  padding: '0.6rem 0.7rem',
                  borderRadius: 'var(--radius)',
                  border: `1px solid ${on ? 'hsl(var(--primary))' : 'hsl(var(--border))'}`,
                  background: on ? 'hsl(var(--primary) / 0.08)' : '#fff',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontSize: '0.88rem', fontWeight: 600, color: on ? 'hsl(var(--primary))' : 'hsl(var(--foreground))' }}>{k.label}</div>
                <div style={{ fontSize: '0.74rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.15rem' }}>{k.helper}</div>
              </button>
            );
          })}
        </div>

        <Row label={kind === 'custom' ? 'Message (required)' : 'Extra message (optional)'}>
          <textarea
            rows={3}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={kind === 'custom'
              ? 'Type the update you want to send to parents…'
              : 'Add any extra context that will be appended to the email body.'}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
            data-testid="notify-message-input"
          />
        </Row>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={send}
            disabled={busy || attendeeCount === 0}
            data-testid="notify-send-btn"
            style={{ padding: '0.5rem 1.1rem' }}
          >{busy ? 'Sending…' : '🚀 Send to parents'}</button>
        </div>
      </div>

      <h4 style={{ margin: '0.6rem 0 0.45rem 0', fontSize: '0.9rem', color: 'hsl(var(--muted-foreground))' }}>
        Notification history ({(notificationsLog || []).length})
      </h4>
      {(notificationsLog || []).length === 0 ? (
        <div className="empty-state" style={{ padding: '1.25rem' }} data-testid="notify-history-empty">
          No notifications sent for this event yet.
        </div>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }} data-testid="notify-history-list">
          {[...(notificationsLog || [])].reverse().map((entry, idx) => (
            <li key={idx} className="card" style={{ marginBottom: '0.45rem' }} data-testid={`notify-history-${idx}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <strong style={{ fontSize: '0.88rem' }}>
                  {(kindOptions.find((k) => k.v === entry.kind) || {}).label || entry.kind}
                </strong>
                <span style={{ fontSize: '0.74rem', color: 'hsl(var(--muted-foreground))' }}>
                  {formatPostDate(entry.sent_at)}{entry.sent_by_name ? ' · ' + entry.sent_by_name : ''}
                </span>
              </div>
              <div style={{ marginTop: '0.3rem', fontSize: '0.82rem', display: 'flex', gap: '0.45rem', flexWrap: 'wrap' }}>
                <span style={{ background: 'hsl(140 70% 92%)', color: 'hsl(140 55% 26%)', padding: '0.1rem 0.5rem', borderRadius: 999, fontSize: '0.74rem', fontWeight: 600 }}>✓ {entry.ok || 0} sent</span>
                {entry.failed > 0 && (
                  <span style={{ background: 'hsl(0 75% 94%)', color: 'hsl(0 60% 35%)', padding: '0.1rem 0.5rem', borderRadius: 999, fontSize: '0.74rem', fontWeight: 600 }}>✗ {entry.failed} failed</span>
                )}
                {entry.skipped > 0 && (
                  <span style={{ background: 'hsl(45 95% 92%)', color: 'hsl(35 80% 30%)', padding: '0.1rem 0.5rem', borderRadius: 999, fontSize: '0.74rem', fontWeight: 600 }}>⚠ {entry.skipped} skipped</span>
                )}
                <span style={{ color: 'hsl(var(--muted-foreground))' }}>· {entry.recipients} recipient{entry.recipients === 1 ? '' : 's'}</span>
              </div>
              {entry.message && (
                <div style={{ marginTop: '0.4rem', fontSize: '0.82rem', padding: '0.4rem 0.55rem', background: 'hsl(var(--muted) / 0.4)', borderRadius: 6, fontStyle: 'italic' }}>
                  “{entry.message}”
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------- Layout helpers ----------------

function Row({ label, children }) {
  return (
    <div style={{ marginBottom: '0.7rem' }}>
      <div style={labelStyle}>{label}</div>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: '0.55rem 0.7rem',
  border: '1px solid hsl(var(--border))',
  borderRadius: 'var(--radius)',
  background: '#fff',
  fontSize: '0.92rem',
  boxSizing: 'border-box',
};

const labelStyle = {
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  color: 'hsl(var(--muted-foreground))',
  letterSpacing: '0.04em',
  marginBottom: '0.2rem',
  fontWeight: 600,
};

export default EventDrawer;
